import { useEffect, useRef } from 'react';

import { ApiError } from '../../api/games.js';
import { ErrorNote } from '../../game/chrome.js';
import { DATA_TAG } from '../../theme/type.js';
import type { SeatAlert } from '../model/alerts.js';

/**
 * The alert lane (design 158–163).
 *
 * One channel, owned by the shell and raised into by windows. Every alert goes
 * through the same `ErrorNote` the entry pages use, so an `ErrorCode` and its
 * `correlationId` reach the screen the same way wherever they were raised
 * (§16.3) — that is the difference between "it said something went wrong" and a
 * one-minute investigation.
 *
 * Positioned rather than in flow, and it animates on `transform` and `opacity`
 * only. A lane that reflowed the chrome on arrival would move the commit button
 * out from under a cursor already travelling towards it, with a five-minute
 * clock running.
 *
 * It floats over the **centre pane** rather than over the whole console, which
 * the design's fixed `top: 140` would do. Two bands below the tab strip are
 * conditional — the read-only banner and the GM control strip — so a fixed
 * offset lands on top of the GM's own buttons exactly when something has gone
 * wrong and they most need them.
 */

export interface AlertLaneProps {
  readonly alerts: readonly SeatAlert[];
  readonly onDismiss: (key: string) => void;
  /** How many the cap hid. Rendered, not swallowed. */
  readonly overflow: number;
  /** Reports the lane's real height so the toast can clear it. */
  readonly onHeight: (height: number) => void;
}

export function AlertLane(props: AlertLaneProps): JSX.Element | null {
  const { onHeight } = props;
  const ref = useRef<HTMLDivElement>(null);
  const empty = props.alerts.length === 0;

  /*
   * The toast used to clear the lane with a guessed constant
   * (`TOAST_TOP_WITH_ALERT = 76`), justified on the grounds that a banner is a
   * title over one short sentence and so has a bounded height. An `ErrorNote`
   * with a title, a sentence *and* a `correlationId` exceeds it, and the toast
   * slid underneath exactly when something had gone wrong. A `ResizeObserver` is
   * cheaper than being wrong, and the lane is the only element that knows.
   */
  useEffect(() => {
    const node = ref.current;
    if (node === null) {
      onHeight(0);
      return;
    }
    const observer = new ResizeObserver(() => onHeight(node.getBoundingClientRect().height));
    observer.observe(node);
    onHeight(node.getBoundingClientRect().height);
    return () => observer.disconnect();
  }, [onHeight, empty, props.alerts.length, props.overflow]);

  if (empty) return null;

  return (
    <div
      ref={ref}
      data-testid="errors"
      style={{
        position: 'absolute',
        left: '50%',
        top: 12,
        zIndex: 'var(--z-overlay)',
        width: 'min(680px, calc(100% - 48px))',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        transform: 'translateX(-50%)',
        animation: 'bs-arrive .24s var(--ease)',
        // The lane floats over the pane's own header band, and its box is wider
        // and taller than the notes inside it. Without this it swallowed clicks
        // on whatever it covered — measured against the MW board, `WINDOW
        // CLOSING` put an unclickable 680 × 57 rectangle over `TIMELINE` and
        // `RECOGNITION` for the last minute of every window, which is the minute
        // the warning seat is deciding in. `Toast` has always had this line; the
        // lane is the same kind of object and needs it for the same reason.
        // Restored per note below, so the notes themselves stay clickable.
        pointerEvents: 'none',
      }}
    >
      {props.overflow > 0 && (
        <span
          data-testid="alert-overflow"
          style={{
            ...DATA_TAG,
            alignSelf: 'center',
            padding: '3px 8px',
            background: 'var(--surface-3)',
            border: '1px solid var(--border-strong)',
            color: 'var(--text-muted)',
            boxShadow: 'var(--shadow)',
            pointerEvents: 'auto',
          }}
        >
          {`+${String(props.overflow)} MORE`}
        </span>
      )}
      {props.alerts.map((alert) => (
        <div
          key={alert.key}
          style={{
            position: 'relative',
            boxShadow: 'var(--shadow)',
            background: 'var(--surface-3)',
            // The note is opaque and visible, so it may take the pointer; the
            // lane's slack around it may not.
            pointerEvents: 'auto',
          }}
        >
          <ErrorNote
            error={
              alert.code === null
                ? alert.body
                : new ApiError(0, alert.code, alert.body, alert.correlationId ?? undefined)
            }
            tone={alert.tone}
            title={alert.title}
          />
          <button
            type="button"
            data-testid="alert-dismiss"
            onClick={() => props.onDismiss(alert.key)}
            style={{
              position: 'absolute',
              top: 7,
              right: 8,
              height: 20,
              padding: '0 10px',
              background: 'transparent',
              border: `1px solid ${alert.tone === 'warning' ? 'var(--warn-tint-border)' : 'var(--red)'}`,
              color: alert.tone === 'warning' ? 'var(--warn)' : 'var(--red-text)',
              fontFamily: 'var(--font-ui)',
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: '.14em',
              textTransform: 'uppercase',
            }}
          >
            Dismiss
          </button>
        </div>
      ))}
    </div>
  );
}
